import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Switch,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
  Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

const AssignmentMessaging = ({ teamUid, clientUID }: { teamUid: any, clientUID: any }) => {
  const hasPermission = useHasPermission(PERMISSIONS.ADD_EDIT_NOTIFICATIONS);

  const {
    getFlowByTeamUid,
    getResourceTypes,
    createAssignmentMessagingConfig,
    getAssignmentMessagingConfigs,
    updateAssignmentMessagingConfig,
    deleteAssignmentMessagingConfig
  } = useApi();

  const [fieldOptions, setFieldOptions] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [configs, setConfigs] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [loadingOverview, setLoadingOverview] = useState(false);

  useEffect(() => {
    if (teamUid) {
      fetchConfigs(teamUid);
      fetchFlowData();
    }
  }, [teamUid]);

  useEffect(() => {
    if (clientUID) {
      fetchRoles(clientUID);
    }
  }, [clientUID]);

  const fetchFlowData = async () => {
    if (!teamUid) return;
    try {
      const flowResp = await getFlowByTeamUid(teamUid);
      if (flowResp?.data?.steps) {
        setFieldOptions(flowResp.data.steps);
      }
    } catch (error) {
      console.error('Error fetching team flow data:', error);
    }
  };

  const fetchConfigs = async (teamUid: string) => {
    setLoadingOverview(true);
    try {
      const response = await getAssignmentMessagingConfigs(teamUid);
      setConfigs(response.data || []);
    } catch (error) {
      console.error('Error fetching assignment messaging configs:', error);
    }
    setLoadingOverview(false);
  };

  const fetchRoles = async (clientUID: string) => {
    try {
      const response = await getResourceTypes(clientUID, 'user');
      if (Array.isArray(response?.data)) {
        setRolesList(response.data.map((role: any) => ({
          uid: role.uid,
          id: role.uid,
          name: role.name,
          roleName: role.name
        })));
      }
    } catch (e) {
      console.error('Error fetching roles:', e);
    }
  };

  const formik = useFormik({
    initialValues: {
      enabled: true,
      roles: [] as string[],
      templateFields: [] as string[],
      template: '',
      scheduleEnabled: false,
      scheduleField: '',
      triggerStep: '',
      forTransactionAssignment: false,
      operator: 'subtract',
      value: 1,
      valueType: 'days',
      timeOfDay: ''
    },
    validationSchema: Yup.object({
      triggerStep: Yup.string().required('Trigger step is required'),
      roles: Yup.array().min(1, 'At least one role is required').required('Role is required'),
      template: Yup.string().trim().required('Message template is required'),
      scheduleField: Yup.string().when('scheduleEnabled', {
        is: true,
        then: (schema) => schema.required('Date field is required when scheduling is enabled')
      })
    }),
    onSubmit: async (values) => {
      const parseOne = (item: any) => {
        try { return JSON.parse(item); } catch { return item; }
      };
      const submitValues = {
        enabled: values.enabled,
        roles: values.roles,
        template: values.template,
        templateFields: values.templateFields.map(parseOne),
        triggerStep: values.triggerStep ? parseOne(values.triggerStep) : null,
        forTransactionAssignment: values.forTransactionAssignment,
        scheduleCondition: values.scheduleEnabled && values.scheduleField
          ? {
              field: parseOne(values.scheduleField),
              operator: values.operator,
              value: Number(values.value) || 0,
              valueType: values.valueType,
              ...(values.timeOfDay ? { timeOfDay: values.timeOfDay } : {})
            }
          : null
      };
      try {
        if (editingId) {
          await updateAssignmentMessagingConfig(editingId, teamUid, submitValues);
          setEditingId(null);
          setSnackbarMsg('Assignment messaging updated successfully');
        } else {
          await createAssignmentMessagingConfig(teamUid, submitValues);
          setSnackbarMsg('Assignment messaging created successfully');
        }
        setSnackbarOpen(true);
        formik.resetForm();
        await fetchConfigs(teamUid);
      } catch (err) {
        setSnackbarMsg('Error saving assignment messaging');
        setSnackbarOpen(true);
        console.error('Error saving assignment messaging config:', err);
      }
    }
  });

  const handleEdit = (row: any) => {
    setEditingId(row.id);
    formik.setValues({
      enabled: Boolean(row.enabled),
      roles: Array.isArray(row.roles) ? row.roles : [],
      templateFields: Array.isArray(row.templateFields)
        ? row.templateFields.map((f: any) => JSON.stringify({ question: f.question, uid: f.uid }))
        : [],
      template: row.template || '',
      triggerStep: row.triggerStep
        ? JSON.stringify({ title: row.triggerStep.title, uid: row.triggerStep.uid })
        : '',
      forTransactionAssignment: row.forTransactionAssignment === true,
      scheduleEnabled: Boolean(row.scheduleCondition),
      scheduleField: row.scheduleCondition?.field
        ? JSON.stringify({
            question: row.scheduleCondition.field.question,
            uid: row.scheduleCondition.field.uid
          })
        : '',
      operator: row.scheduleCondition?.operator || 'subtract',
      value: row.scheduleCondition?.value ?? 1,
      valueType: row.scheduleCondition?.valueType || 'days',
      timeOfDay: row.scheduleCondition?.timeOfDay || ''
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAssignmentMessagingConfig(id);
      setSnackbarMsg('Assignment messaging deleted successfully');
      setSnackbarOpen(true);
      await fetchConfigs(teamUid);
    } catch (err) {
      setSnackbarMsg('Error deleting assignment messaging');
      setSnackbarOpen(true);
      console.error('Error deleting assignment messaging config:', err);
    }
  };

  const insertToken = (question: string) => {
    formik.setFieldValue('template', `${formik.values.template}{{${question}}}`);
  };

  // Flattened DATE fields for the schedule picker, grouped by step title.
  const dateFieldOptions = (Array.isArray(fieldOptions) ? fieldOptions : []).flatMap((step: any) =>
    (step.component?.fields || [])
      .filter((f: any) => f?.inputComponent === 'DATE')
      .map((f: any) => ({
        label: f.question,
        group: step.title,
        value: JSON.stringify({ question: f.question, uid: f.uid })
      }))
  );

  const fieldUid = (jsonStr: string) => {
    try { return JSON.parse(jsonStr).uid; } catch { return null; }
  };

  const selectedDateField =
    dateFieldOptions.find((o) => fieldUid(o.value) === fieldUid(formik.values.scheduleField)) || null;

  // Steps whose completion can trigger a message.
  const stepOptions = (Array.isArray(fieldOptions) ? fieldOptions : []).map((step: any) => ({
    label: step.title,
    value: JSON.stringify({ title: step.title, uid: step.uid })
  }));

  const selectedTriggerStep =
    stepOptions.find((o) => fieldUid(o.value) === fieldUid(formik.values.triggerStep)) || null;

  const scheduleSummary = (condition: any) => {
    if (!condition) return 'On assignment';
    const sign = condition.operator === 'subtract' ? '-' : '+';
    const time = condition.timeOfDay ? ` at ${condition.timeOfDay}` : '';
    return `${condition.field?.question}: ${sign}${condition.value} ${condition.valueType}${time}`;
  };

  return (
    <Box p={2}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarMsg.includes('Error') ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Typography variant="h5" gutterBottom>
        Assignment Messaging
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Send an SMS to a transaction&apos;s assignees when a step completes. Pick the step, pick the roles that should hear about it, and
        insert form field values into the message with the field chips below.
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.enabled}
                onChange={(e) => formik.setFieldValue('enabled', e.target.checked)}
              />
            }
            label="Enabled for this process"
          />
          <Autocomplete
            size="small"
            options={stepOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => fieldUid(option.value) === fieldUid(value.value)}
            value={selectedTriggerStep}
            onChange={(_, newValue) => formik.setFieldValue('triggerStep', newValue ? newValue.value : '')}
            onBlur={formik.handleBlur}
            noOptionsText="No steps in this flow"
            renderInput={(params) => (
              <TextField
                {...params}
                name="triggerStep"
                label="Send when this step completes"
                placeholder="Pick the step whose completion sends the message"
                error={Boolean(formik.touched.triggerStep && formik.errors.triggerStep)}
                helperText={
                  (formik.touched.triggerStep && formik.errors.triggerStep) ||
                  'The message goes out when this step is completed, not when someone is assigned'
                }
              />
            )}
          />
          <FormControl fullWidth size="small" error={Boolean(formik.touched.roles && formik.errors.roles)}>
            <InputLabel id="roles-label">Roles that receive the message</InputLabel>
            <Select
              labelId="roles-label"
              id="roles"
              name="roles"
              multiple
              value={formik.values.roles}
              label="Roles that receive the message"
              onChange={(e) => formik.setFieldValue('roles', e.target.value)}
              onBlur={formik.handleBlur}
              renderValue={(selected) =>
                (selected as string[])
                  .map((sel) => {
                    const found = rolesList.find((role: any) => role.uid === sel || role.id === sel);
                    return found ? found.roleName || found.name : sel;
                  })
                  .join(', ')
              }
            >
              {rolesList.length > 0 ? (
                rolesList.map((role: any) => (
                  <MenuItem key={role.uid || role.id} value={role.uid || role.id}>
                    {role.roleName || role.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No roles available
                </MenuItem>
              )}
            </Select>
            {formik.touched.roles && formik.errors.roles ? (
              <Typography color="error" variant="caption">
                {formik.errors.roles as string}
              </Typography>
            ) : (
              <Typography variant="caption" color="textSecondary">
                Everyone assigned to this transaction holding one of these roles gets the message
              </Typography>
            )}
          </FormControl>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.forTransactionAssignment}
                  onChange={(e) => formik.setFieldValue('forTransactionAssignment', e.target.checked)}
                  name="forTransactionAssignment"
                />
              }
              label="For transaction assignment"
            />
            <Typography
              variant="caption"
              color={formik.values.forTransactionAssignment ? 'textSecondary' : 'warning.main'}
              display="block"
            >
              {formik.values.forTransactionAssignment
                ? 'Only people assigned to the transaction who hold one of the roles above.'
                : 'Warning: sends to EVERY holder of those roles on this team, whether assigned to the transaction or not.'}
            </Typography>
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel id="template-fields-label">Form fields available in the message</InputLabel>
            <Select
              labelId="template-fields-label"
              id="templateFields"
              name="templateFields"
              multiple
              value={formik.values.templateFields}
              label="Form fields available in the message"
              onChange={(e) => formik.setFieldValue('templateFields', e.target.value)}
              renderValue={(selected) =>
                (selected as string[])
                  .map((sel) => {
                    try { return JSON.parse(sel).question || ''; } catch { return sel; }
                  })
                  .join(', ')
              }
            >
              {Array.isArray(fieldOptions) &&
                fieldOptions.map((step, idx2) => {
                  const allFields = step.component?.fields || [];
                  if (allFields.length === 0) return null;
                  return [
                    <MenuItem key={step.title + idx2} disabled divider>
                      <strong>{step.title}</strong>
                    </MenuItem>,
                    ...allFields.map((field: any) => (
                      <MenuItem
                        key={field._id}
                        value={JSON.stringify({ question: field.question, uid: field.uid })}
                      >
                        {field.question}
                      </MenuItem>
                    ))
                  ];
                })}
            </Select>
          </FormControl>
          {formik.values.templateFields.length > 0 && (
            <Box>
              <Typography variant="caption" color="textSecondary">
                Click a field to insert it into the message:
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {formik.values.templateFields.map((sel) => {
                  let question = sel;
                  try { question = JSON.parse(sel).question; } catch { /* keep raw */ }
                  return (
                    <Chip
                      key={sel}
                      label={question}
                      size="small"
                      onClick={() => insertToken(question)}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          <TextField
            id="template"
            name="template"
            label="Message template"
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={formik.values.template}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.template && formik.errors.template)}
            helperText={(formik.touched.template && formik.errors.template) as string}
            placeholder={'Hi,\n\nYou have been assigned shipment number {{Shipment Number}}.\nShipment to be delivered on {{Delivery Date}}.'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.scheduleEnabled}
                onChange={(e) => formik.setFieldValue('scheduleEnabled', e.target.checked)}
              />
            }
            label="Schedule relative to a date field (otherwise sends on assignment)"
          />
          {formik.values.scheduleEnabled && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <Autocomplete
                  size="small"
                  options={dateFieldOptions}
                  groupBy={(option) => option.group}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => fieldUid(option.value) === fieldUid(value.value)}
                  value={selectedDateField}
                  onChange={(_, newValue) => formik.setFieldValue('scheduleField', newValue ? newValue.value : '')}
                  noOptionsText="No date fields in this flow"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Date Field"
                      placeholder="Search date fields…"
                      error={Boolean(formik.touched.scheduleField && formik.errors.scheduleField)}
                      helperText={(formik.touched.scheduleField && formik.errors.scheduleField) as string}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={formik.values.operator}
                    onChange={(e) => formik.setFieldValue('operator', e.target.value)}
                  >
                    <MenuItem value="add">+</MenuItem>
                    <MenuItem value="subtract">-</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  name="value"
                  type="number"
                  size="small"
                  value={formik.values.value}
                  onChange={formik.handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <Select
                    value={formik.values.valueType}
                    onChange={(e) => formik.setFieldValue('valueType', e.target.value)}
                  >
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <Tooltip title="Optional: send at this time of day">
                  <TextField
                    fullWidth
                    name="timeOfDay"
                    type="time"
                    size="small"
                    value={formik.values.timeOfDay}
                    onChange={formik.handleChange}
                    InputLabelProps={{ shrink: true }}
                    label="Time"
                  />
                </Tooltip>
              </Grid>
            </Grid>
          )}
          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: 'secondary.dark', width: '100px', minWidth: '50px' }}
              disabled={!hasPermission}
            >
              Save
            </Button>
          </Box>
        </Stack>
      </form>
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          Assignment Messaging Overview
        </Typography>
        <TableContainer component={Paper}>
          {loadingOverview ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Enabled</TableCell>
                  <TableCell>On Step</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((row: any) => (
                  <TableRow key={row.uid || row.id}>
                    <TableCell>
                      <Chip label={row.enabled ? 'On' : 'Off'} color={row.enabled ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      {row.triggerStep?.title || (
                        <Tooltip title="No trigger step, so this config never sends. Edit it and pick the step whose completion should send the message.">
                          <Chip label="Dormant" size="small" color="warning" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {Array.isArray(row.roles)
                        ? row.roles.map((r: string, idx: number) => {
                            const found = rolesList.find((role: any) => role.id === r || role.uid === r);
                            return <Chip key={r + idx} label={found ? (found.roleName || found.name) : r} size="small" sx={{ mr: 0.5 }} />;
                          })
                        : ''}
                    </TableCell>
                    <TableCell>
                      {row.forTransactionAssignment === true ? (
                        <Chip label="Transaction assignees" size="small" variant="outlined" />
                      ) : (
                        <Tooltip title="Sends to every holder of these roles on the team, assigned or not">
                          <Chip label="All role holders" size="small" color="warning" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={row.template || ''}>
                        <span>{row.template}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{scheduleSummary(row.scheduleCondition)}</TableCell>
                    <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(row)}
                        disabled={!hasPermission}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.id)}
                        disabled={!hasPermission}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
};

export default AssignmentMessaging;
